"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { SITE_TOOL_ENTRIES } from "@/lib/site-config";

const orbitTools = SITE_TOOL_ENTRIES.filter(
  (tool) => tool.name !== "SQL" && tool.name !== "Tableau",
);
const ring1Icons = orbitTools.slice(0, Math.ceil(orbitTools.length / 2));
const ring2Icons = orbitTools.slice(Math.ceil(orbitTools.length / 2));

const getLogoStyles = (url: string) => {
  const lowercase = url.toLowerCase();
  if (lowercase.includes("ml")) {
    return {
      padding: "p-0.5 sm:p-1",
      scale: "scale-[1.15] sm:scale-[1.25]",
    };
  }
  return {
    padding: "p-1 sm:p-1.5",
    scale: "scale-100",
  };
};

export function OrbitingIcons() {
  const containerRef = useRef<HTMLDivElement>(null);
  const ring1Ref = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      let rx = 180;
      let ry = 70;

      const updateDimensions = () => {
        const ringEl = ring1Ref.current;
        if (ringEl) {
          rx = ringEl.offsetWidth / 2;
          ry = ringEl.offsetHeight / 2;
        }
      };

      updateDimensions();

      const resizeObserver =
        typeof ResizeObserver !== "undefined" && ring1Ref.current
          ? new ResizeObserver(updateDimensions)
          : null;
      resizeObserver?.observe(ring1Ref.current!);
      window.addEventListener("resize", updateDimensions);

      const ring1Items = gsap.utils.toArray<HTMLElement>(".ring-1-item");
      ring1Items.forEach((item, i) => {
        const startAngle = (i / ring1Items.length) * Math.PI * 2;
        const obj = { angle: startAngle };

        gsap.to(obj, {
          angle: startAngle + Math.PI * 2,
          duration: 40,
          repeat: -1,
          ease: "none",
          onUpdate: () => {
            const x = Math.cos(obj.angle) * rx;
            const y = Math.sin(obj.angle) * ry;
            gsap.set(item, { x, y });
          },
        });
      });

      const ring2Items = gsap.utils.toArray<HTMLElement>(".ring-2-item");
      ring2Items.forEach((item, i) => {
        const startAngle = (i / ring2Items.length) * Math.PI * 2;
        const obj = { angle: startAngle };

        gsap.to(obj, {
          angle: startAngle - Math.PI * 2,
          duration: 50,
          repeat: -1,
          ease: "none",
          onUpdate: () => {
            const x = Math.cos(obj.angle) * rx;
            const y = Math.sin(obj.angle) * ry;
            gsap.set(item, { x, y });
          },
        });
      });

      return () => {
        resizeObserver?.disconnect();
        window.removeEventListener("resize", updateDimensions);
      };
    },
    { scope: containerRef },
  );

  const ringClasses =
    "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(92vw,1200px)] h-[140px] sm:h-[200px] md:h-[280px] lg:h-[400px]";

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 z-0 pointer-events-none flex items-center justify-center"
      aria-hidden
    >
      {/* Ring 1 (Tilted -30deg) */}
      <div
        ref={ring1Ref}
        className={`orbit-ring ${ringClasses} rotate-[-30deg]`}
      >
        <div
          className="h-full w-full border border-foreground/10 dark:border-white/20 sm:border-2"
          style={{ borderRadius: "50%" }}
        />

        {ring1Icons.map((tool) => {
          const icon = tool.iconUrl || "";
          const styles = getLogoStyles(icon);
          return (
            <div
              key={`ring1-${tool.name}`}
              className={`ring-1-item absolute top-1/2 left-1/2 flex h-7 w-7 -ml-3.5 -mt-3.5 items-center justify-center sm:h-10 sm:w-10 sm:-ml-5 sm:-mt-5 md:h-12 md:w-12 md:-ml-6 md:-mt-6 ${styles.padding}`}
            >
              <div className="h-full w-full rotate-[30deg]">
                <img
                  src={icon}
                  alt=""
                  className={`h-full w-full object-contain drop-shadow-sm ${styles.scale}`}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Ring 2 (Tilted 30deg) */}
      <div className={`${ringClasses} rotate-[30deg]`}>
        <div
          className="h-full w-full border border-foreground/10 dark:border-white/20 sm:border-2"
          style={{ borderRadius: "50%" }}
        />

        {ring2Icons.map((tool) => {
          const icon = tool.iconUrl || "";
          const styles = getLogoStyles(icon);
          return (
            <div
              key={`ring2-${tool.name}`}
              className={`ring-2-item absolute top-1/2 left-1/2 flex h-7 w-7 -ml-3.5 -mt-3.5 items-center justify-center sm:h-10 sm:w-10 sm:-ml-5 sm:-mt-5 md:h-12 md:w-12 md:-ml-6 md:-mt-6 ${styles.padding}`}
            >
              <div className="h-full w-full rotate-[-30deg]">
                <img
                  src={icon}
                  alt=""
                  className={`h-full w-full object-contain drop-shadow-sm ${styles.scale}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
